import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { ChatMessage, LegacyGenerationJob } from "~/entities/chat";
import { useChatJobActions } from "~/features/chat-job-actions/useChatJobActions";
import { useMessageSelection } from "~/features/move-messages";
import { useRepeatGeneration } from "~/features/repeat-generation";
import { toDisplayPrompt } from "~/shared/lib/element-mentions";
import { cn } from "~/shared/lib/utils";
import { Checkbox } from "~/shared/ui/checkbox";
import { useChatMessageError } from "~/shared/store/useChatErrorsStore";

import { MessageArtifacts } from "./MessageArtifacts";
import { MessageAttachments } from "./MessageAttachments";
import { MessageBubbleActions } from "./MessageBubbleActions";
import { MessageCanceled } from "./MessageCanceled";
import { MessageContent } from "./MessageContent";
import { MessageError } from "./MessageError";
import { MessageInputs } from "./MessageInputs";
import { MessageJobMetaRow } from "./MessageJobMetaRow";
import { MessageProgress } from "./MessageProgress";
import { useMessageInputs } from "./useMessageInputs";

interface MessageItemProps {
    message: ChatMessage;
    heroMedia: boolean;
    job: LegacyGenerationJob | null;
    onRetry: (message: ChatMessage) => void;
    isRetrying?: boolean;
    // Нижнее сообщение ленты: его медиа грузится приоритетно (см.
    // VirtualMessageList) — открытый чат показывает именно его.
    isPriorityMedia?: boolean;
}

export function MessageItem({
    message,
    heroMedia,
    job,
    onRetry,
    isRetrying,
    isPriorityMedia = false,
}: MessageItemProps) {
    const { t } = useTranslation("chat");
    const isUser = message.role === 1;
    const isSystem = message.role === 0;
    const inputs = useMessageInputs(job, message.attachments);
    const referenceElements = useMemo(
        () => job?.requestPayload.referenceElements ?? [],
        [job?.requestPayload.referenceElements],
    );
    // Промпт хранит ссылку по id (`@[element:…]`), человеку показываем имя. В
    // истории берём его из снимка запроса, а не из библиотеки: текст должен
    // совпадать с чипами под ним и говорить, что реально ушло в генерацию.
    const displayPrompt = useMemo(
        () =>
            toDisplayPrompt(
                message.prompt,
                referenceElements
                    .filter((element): element is typeof element & { elementId: string } =>
                        Boolean(element.elementId),
                    )
                    .map((element) => ({ id: element.elementId, name: element.name })),
            ),
        [message.prompt, referenceElements],
    );
    const hasArtifacts = !!job && job.artifacts.length > 0;
    const isJobInFlight = !!job && (job.status === "queued" || job.status === "in_progress");
    const isJobRecovering = !!job && job.status === "recovering";
    const isJobCanceled = !!job && job.status === "canceled";
    const isJobErroredOut = !!job && (job.status === "failed" || job.status === "expired");
    const localError = useChatMessageError(message.id);
    const showError = !!localError || isJobErroredOut;

    const jobActions = useChatJobActions(message.chatId);

    // «Повторить запрос» — только у сообщений с воспроизводимой
    // генерацией: у импортированных и старых джоб модель/режим могут быть
    // недоступны, тогда действие не показываем вовсе.
    const { canRepeat, repeat } = useRepeatGeneration();
    const onRepeat = canRepeat(message, job) ? () => repeat(message, job) : undefined;

    // Импортированные сообщения перемещаемы → под них резервируем
    // слот в мета-строке (у всех пользователей, чтобы дата/копирование/шеринг
    // стояли на одном месте). Сам чек-бокс с галочкой виден только под
    // MessageSelectionProvider (суперадмин); сгенерированные сообщения не
    // перемещаются и слота не получают.
    const selection = useMessageSelection();
    const isMovable = !!job?.isImported;
    const canSelect = !!selection && isMovable;
    const isSelected = canSelect && selection.isSelected(message.id);

    if (isSystem) {
        return (
            <div data-msg-anchor={message.id} className="px-4 py-2 text-xs text-zinc-500 italic">
                {message.prompt}
            </div>
        );
    }

    const timestamp = new Date(message.createdAt).toLocaleString();

    return (
        <div className="group py-3">
            <div
                data-msg-anchor={message.id}
                className={cn(
                    "relative rounded-2xl transition-colors",
                    isUser
                        ? cn(
                              "ml-auto border border-white/5 bg-white/[0.04] px-4 py-3",
                              heroMedia ? "max-w-[90%] md:max-w-[60%]" : "max-w-[90%] md:max-w-[70%]",
                          )
                        : "w-full",
                    isSelected && "ring-1 ring-primary/40",
                )}
            >
                <MessageContent text={displayPrompt} />

                {/* Входы генерации одним блоком: кадры с ролями, элементы,
                    референсы — всё из снимка запроса. Пустые каналы не рисуются,
                    у генераций без снимка блока нет вовсе. */}
                {inputs.channels.length > 0 && (
                    <div className="mt-2">
                        <MessageInputs channels={inputs.channels} artifactType={inputs.artifactType} />
                    </div>
                )}

                {/* Вложения вне каналов (аудио, документы, видео) — прежним списком. */}
                {inputs.otherFiles.length > 0 && (
                    <div className="mt-2">
                        <MessageAttachments attachments={inputs.otherFiles} />
                    </div>
                )}

                <div
                    className={cn(
                        "mt-1 flex items-center gap-2 text-xs",
                        isUser ? "justify-end" : "justify-start",
                    )}
                >
                    {!isUser && (
                        <MessageBubbleActions text={displayPrompt} align="left" onRepeat={onRepeat} />
                    )}
                    <span className="opacity-40">{timestamp}</span>
                    {isUser && (
                        <MessageBubbleActions text={displayPrompt} align="right" onRepeat={onRepeat} />
                    )}
                    {/* Зарезервированный слот под чек-бокс: место постоянно для
                        всех перемещаемых сообщений, галочка — только суперадмину. */}
                    {isMovable && (
                        <span className="flex size-5 shrink-0 items-center justify-center">
                            {canSelect && (
                                <Checkbox
                                    checked={isSelected}
                                    onCheckedChange={() => selection?.toggle(message.id)}
                                    aria-label={t("moveMessages.selectAria")}
                                    className="size-4"
                                />
                            )}
                        </span>
                    )}
                </div>
            </div>

            {showError && (
                <MessageError
                    messageId={message.id}
                    job={job}
                    localError={localError}
                    onRetry={() => onRetry(message)}
                    isRetrying={isRetrying}
                />
            )}

            {(isJobInFlight || isJobRecovering) && job && !showError && (
                <MessageProgress
                    job={job}
                    onCancel={isJobInFlight ? () => void jobActions.cancel(job.id) : undefined}
                    isCancelling={jobActions.isCancelling}
                />
            )}

            {isJobCanceled && job && !showError && (
                <MessageCanceled
                    job={job}
                    onRetry={() => void jobActions.retry(job.id)}
                    isRetrying={jobActions.isRetrying}
                />
            )}

            {hasArtifacts && job && !showError && (
                <div className="mt-3">
                    <MessageArtifacts
                        artifacts={job.artifacts}
                        isPendingStorage={isJobRecovering}
                        isPriorityMedia={isPriorityMedia}
                        isImported={job.isImported}
                    />
                    <MessageJobMetaRow job={job} />
                </div>
            )}
        </div>
    );
}
